import axios from 'axios';
import { NormsInfo } from '../types/NormsInfo';

// Default to Node backend on port 4000, but allow override via env
const API_BASE_URL = "https://www.truenorms.com/api";

  /** Normalize date string to YYYY-MM-DD for API. Handles MM/DD/YYYY, MM-DD-YYYY, etc. */
function toApiDate(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return dateStr;
  const trimmed = dateStr.trim();
  if (!trimmed) return dateStr;
  // Already YYYY-MM-DD (starts with 4-digit year)
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmed)) return trimmed;
  // MM/DD/YYYY or MM-DD-YYYY (or other separator)
  const parts = trimmed.split(/[/\-.]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 3) {
    const [a, b, c] = parts;
    const aNum = parseInt(a, 10);
    const bNum = parseInt(b, 10);
    const cNum = parseInt(c, 10);
    // If first part is <= 12 and third is 4 digits, assume MM/DD/YYYY or DD/MM/YYYY
    if (c.length === 4) {
      const year = cNum;
      const month = aNum <= 12 ? aNum : bNum;
      const day = aNum <= 12 ? bNum : aNum;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const y = String(year);
        const m = String(month).padStart(2, '0');
        const d = String(day).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
    // If first part is 4 digits (year), assume YYYY/MM/DD or YYYY-MM-DD
    if (a.length === 4) {
      const year = aNum;
      const month = bNum;
      const day = cNum;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const y = String(year);
        const m = String(month).padStart(2, '0');
        const d = String(day).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
  }
  return dateStr;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Transform request data to handle null/undefined/empty values
  transformRequest: [(data) => {
    // Remove undefined, null, and empty string values before sending
    if (data && typeof data === 'object') {
      const cleaned: any = {};
      Object.keys(data).forEach(key => {
        const value = data[key];
        // Only include defined, non-null, non-empty values
        // Exception: allow 0, false, and valid dates (non-empty strings that are valid dates)
        if (value !== undefined && value !== null && value !== '') {
          // Special handling for fuv - only include if it's a valid date string
          if (key === 'fuv') {
            // Only include fuv if it's a valid ISO date string
            if (typeof value === 'string' && value.length > 0 && value.includes('T')) {
              cleaned[key] = value;
            }
            // Otherwise, don't include it (omit from JSON)
          } else {
            cleaned[key] = value;
          }
        }
      });
      return JSON.stringify(cleaned);
    }
    return JSON.stringify(data);
  }],
});

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login/forgot-password/reset-password errors
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/forgot-password') ||
                          error.config?.url?.includes('/auth/reset-password') ||
                          error.config?.url?.includes('/auth/change-password');
    
    if ((error.response?.status === 401 || error.response?.status === 403) && !isAuthEndpoint) {
      // Token expired or invalid, clear storage and redirect to login
      localStorage.removeItem('token'); 
      localStorage.removeItem('user');
      localStorage.removeItem('historyEditMode');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const normsApi = {
  calculateNorms: async (normsInfo: NormsInfo): Promise<NormsInfo> => {
    const response = await api.post<NormsInfo>('/norms/calculate', normsInfo);
    return response.data;
  },

  generatePdf: async (normsInfo: NormsInfo): Promise<Blob> => {
    const response = await api.post('/norms/generate-pdf', normsInfo, {
      responseType: 'blob',
    });
    return response.data;
  },

  savePatientDetails: async (
    patientId: number,
    userId: number | null,
    data: Record<string, unknown>
  ): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>('/norms/save-patient-details', {
      patientId,
      userId,
      data,
    });
    return response.data;
  },

  savePatientDetailsOnContinue: async (
    patientId: number,
    userId: number | null,
    data: Record<string, unknown>
  ): Promise<{ success: boolean; id: number | null }> => {
    const response = await api.post<{ success: boolean; id: number | null }>('/norms/save-patient-details-on-continue', {
      patientId,
      userId,
      data,
    });
    return response.data;
  },

  // Draft management
  checkDraftForDoctor: async (): Promise<{ hasDraft: boolean; count: number }> => {
    const response = await api.get<{ hasDraft: boolean; count: number }>('/norms/drafts/check');
    return response.data;
  },

  getLatestDraftForDoctor: async (): Promise<{
    hasDraft: boolean;
    patientDetailsId?: number;
    patientId?: number;
    patientName?: string;
    normsData?: NormsInfo | null;
  }> => {
    const response = await api.get<{
      hasDraft: boolean;
      patientDetailsId?: number;
      patientId?: number;
      patientName?: string;
      normsData?: NormsInfo | null;
    }>('/norms/drafts/latest');
    return response.data;
  },

  discardDraftForDoctor: async (patientDetailsId: number): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>('/norms/drafts/discard', {
      patientDetailsId,
    });
    return response.data;
  },

  getSampleData: async (): Promise<NormsInfo> => {
    const response = await api.get<NormsInfo>('/norms/sample-data');
    return response.data;
  },

  healthCheck: async (): Promise<{ status: string; normsLoaded: boolean }> => {
    const response = await api.get('/norms/health');
    return response.data;
  },

  // Lookup lists for dropdowns
  getExaminers: async (): Promise<{ examiners: { id: number; examinerName: string }[] }> => {
    const response = await api.get('/norms/lookups/examiners');
    return response.data;
  },

  getPlaceTesting: async (): Promise<{ places: { id: number; placeName: string }[] }> => {
    const response = await api.get('/norms/lookups/place-testing');
    return response.data;
  },
};

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  verifyOtp: async (email: string, otp: string) => {
    const response = await api.post('/auth/verify-otp', { email, otp });
    return response.data;
  },

  verifyToken: async (token: string) => {
    const response = await api.get('/auth/verify', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  logout: async (token?: string | null) => {
    const response = await api.post(
      '/auth/logout',
      undefined,
      token
        ? {
            headers: { Authorization: `Bearer ${token}` },
          }
        : undefined
    );
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  changePassword: async (newPassword: string) => {
    const response = await api.post('/auth/change-password', { newPassword });
    return response.data;
  },
};

export const adminApi = {
  createUser: async (email: string, phoneNumber: string, firstName: string, lastName: string, fullName: string, location: string, role: 'admin' | 'user', isActive: boolean) => {
    const response = await api.post('/admin/users', { email, phoneNumber, firstName, lastName, fullName, location, role, isActive });
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  updateUserStatus: async (userId: number, isActive: boolean) => {
    const response = await api.patch(`/admin/users/${userId}/status`, { isActive });
    return response.data;
  },

  updateUser: async (
    userId: number,
    data: { email: string; phoneNumber: string; firstName: string; lastName: string; fullName: string; location: string; role: 'admin' | 'user'; isActive: boolean }
  ) => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  // Patient management
  createPatient: async (
    firstName: string,
    lastName: string,
    fullName: string,
    extra?: {
      patSex?: string;
      patRace?: string;
      patDOB?: string;
      patOccupation?: string;
      patEducation?: number;
    }
  ) => {
    // Optionally include addedBy/updatedBy from localStorage if needed by backend
    const storedUser = localStorage.getItem('user');
    let addedBy: number | null = null;
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed && typeof parsed.id === 'number') {
          addedBy = parsed.id;
        }
      } catch {
        addedBy = null;
      }
    }

    const payload: any = { firstName, lastName, fullName };
    if (extra) {
      if (extra.patSex !== undefined) payload.patSex = extra.patSex;
      if (extra.patRace !== undefined) payload.patRace = extra.patRace;
      if (extra.patDOB !== undefined) payload.patDOB = toApiDate(extra.patDOB);
      if (extra.patOccupation !== undefined) payload.patOccupation = extra.patOccupation;
      if (extra.patEducation !== undefined) payload.patEducation = extra.patEducation;
    }
    if (addedBy !== null) {
      payload.addedBy = addedBy;
      payload.updatedBy = addedBy;
    }

    const response = await api.post('/patient/patients', payload);
    return response.data;
  },

  getPatients: async () => {
    const response = await api.get('/admin/patients');
    return response.data;
  },

  /** Admin patient grid: server-side page + total count (default 50 per page). */
  getPatientsPaged: async (params?: { offset?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    const url = query ? `/admin/patients/paged?${query}` : '/admin/patients/paged';
    const response = await api.get(url);
    return response.data as { patients: unknown[]; total: number; limit?: number; offset?: number };
  },


  updatePatient: async (
    patientId: number,
    data: {
      firstName: string;
      lastName: string;
      fullName: string;
      patSex?: string;
      patRace?: string;
      patDOB?: string;
      patOccupation?: string;
      patEducation?: string | number;
    }
  ) => {
    const response = await api.put(`/admin/patients/${patientId}`, data);
    return response.data;
  },

  getPatientsByDoctorId: async (doctorId: number) => {
    const response = await api.get(`/patient/doctor/${doctorId}`);
    return response.data;
  },

  /** Doctor (or admin) patient list: server-side page + total count. */
  getPatientsByDoctorIdPaged: async (
    doctorId: number,
    params?: { offset?: number; limit?: number }
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    const url = query
      ? `/patient/doctor/${doctorId}/paged?${query}`
      : `/patient/doctor/${doctorId}/paged`;
    const response = await api.get(url);
    return response.data as { patients: unknown[]; total: number; limit?: number; offset?: number };
  },


  updatePatientAsUser: async (
    patientId: number,
    data: {
      firstName: string;
      lastName: string;
      fullName: string;
      patSex?: string;
      patRace?: string;
      patDOB?: string;
      patOccupation?: string;
      patEducation?: number;
    }
  ) => {
    const response = await api.put(`/patient/patients/${patientId}`, data);
    return response.data;
  },

  getPatientDetails: async (patientId: number) => {
    const response = await api.get(`/patient/patients/${patientId}/details`);
    return response.data;
  },

  getAuditLog: async (params?: { limit?: number; offset?: number; entityType?: string; action?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    if (params?.entityType) searchParams.set('entityType', params.entityType);
    if (params?.action) searchParams.set('action', params.action);
    const query = searchParams.toString();
    const url = query ? `/admin/audit-log?${query}` : '/admin/audit-log';
    const response = await api.get(url);
    return response.data;
  },

  // Examiner / Place of Testing lookup management (admin only)
  getExaminers: async (): Promise<{ examiners: { id: number; examinerName: string }[] }> => {
    const response = await api.get('/admin/examiners');
    return response.data;
  },

  createExaminer: async (examinerName: string): Promise<{ examiner: { id: number; examinerName: string } }> => {
    const response = await api.post('/admin/examiners', { examinerName });
    return response.data;
  },

  updateExaminer: async (id: number, examinerName: string): Promise<{ examiner: { id: number; examinerName: string } }> => {
    const response = await api.put(`/admin/examiners/${id}`, { examinerName });
    return response.data;
  },

  getPlaceTesting: async (): Promise<{ places: { id: number; placeName: string }[] }> => {
    const response = await api.get('/admin/place-testing');
    return response.data;
  },

  createPlaceTesting: async (placeName: string): Promise<{ place: { id: number; placeName: string } }> => {
    const response = await api.post('/admin/place-testing', { placeName });
    return response.data;
  },

  updatePlaceTesting: async (id: number, placeName: string): Promise<{ place: { id: number; placeName: string } }> => {
    const response = await api.put(`/admin/place-testing/${id}`, { placeName });
    return response.data;
  },
};

export default api;

